import { FC } from "react"
import {
  BadgeSuccess,
  BadgeInfo,
  BadgeDanger,
  BadgeWarning
} from "../../../common/ui/badge"
import {
  ENROLLMENT_STEP_ORDER,
  EnrollmentResponse,
  EnrollmentStatus
} from "../../enroll/types"
import StepGrid from "./step-grid"
import ActionBar from "./action-bar"

const StatusBadge: FC<{ status: EnrollmentStatus }> = ({ status }) => {
  switch (status) {
    case "complete":
      return <BadgeSuccess>Complete</BadgeSuccess>
    case "started":
      return <BadgeInfo>In Progress</BadgeInfo>
    case "partial":
      return <BadgeWarning>Partial</BadgeWarning>
    case "failed":
      return <BadgeDanger>Failed</BadgeDanger>
  }
}

const formatTimestamp = (unix: number): string => {
  const d = new Date(unix * 1000)
  return d.toLocaleString()
}

const formatRelative = (unix: number): string => {
  const diffMs = Date.now() - unix * 1000
  const diffSec = Math.floor(diffMs / 1000)
  if (diffSec < 60) return `${diffSec}s ago`
  const diffMin = Math.floor(diffSec / 60)
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h ago`
  const diffDay = Math.floor(diffHr / 24)
  return `${diffDay}d ago`
}

interface Props {
  enrollment: EnrollmentResponse
  isRetrying: boolean
  onRetry: () => void
  onDelete: () => void
}

const EnrollmentDetail: FC<Props> = ({
  enrollment,
  isRetrying,
  onRetry,
  onDelete
}) => {
  const successCount = ENROLLMENT_STEP_ORDER.filter(
    name => enrollment.steps[name]?.status === "success"
  ).length

  const resourceLinks = [
    enrollment.org_id && {
      label: "Org",
      id: enrollment.org_id,
      href: `/organizations/${enrollment.org_id}`
    },
    enrollment.brand_id && {
      label: "Brand",
      id: enrollment.brand_id,
      href: `/brands/${enrollment.brand_id}`
    },
    enrollment.loid && {
      label: "LO",
      id: enrollment.loid,
      href: `/loan-officers/${enrollment.loid}`
    }
  ].filter(Boolean) as Array<{ label: string; id: number; href: string }>

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="rounded-lg border border-neutral-200 bg-white p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold text-neutral-900">
                Enrollment #{enrollment.enrollment_id}
              </h2>
              <StatusBadge status={enrollment.status} />
            </div>
            <p className="mt-1 text-sm text-neutral-600">
              {enrollment.message}
            </p>
          </div>
          <div className="text-right text-xs text-neutral-500 shrink-0">
            <div>Started {formatRelative(enrollment.started_at)}</div>
            <div>Updated {formatRelative(enrollment.updated_at)}</div>
          </div>
        </div>

        {/* Resource links + metadata */}
        <div className="mt-4 flex items-center gap-4 flex-wrap">
          {resourceLinks.map(link => (
            <a
              key={link.label}
              href={link.href}
              className="inline-flex items-center gap-1.5 rounded-md bg-neutral-100 px-2.5 py-1 text-xs font-mono text-neutral-700 hover:bg-neutral-200 transition-colors"
            >
              {link.label} #{link.id}
            </a>
          ))}
          <span className="text-xs text-neutral-400">
            {successCount} of {ENROLLMENT_STEP_ORDER.length} steps complete
          </span>
        </div>

        {/* Timestamps */}
        <div className="mt-3 pt-3 border-t border-neutral-100 flex gap-6 text-xs text-neutral-500">
          <span>
            Started:{" "}
            <span className="font-mono">
              {formatTimestamp(enrollment.started_at)}
            </span>
          </span>
          <span>
            Updated:{" "}
            <span className="font-mono">
              {formatTimestamp(enrollment.updated_at)}
            </span>
          </span>
        </div>
      </div>

      {/* Step grid */}
      <StepGrid enrollment={enrollment} />

      {/* Actions */}
      <ActionBar
        enrollment={enrollment}
        isRetrying={isRetrying}
        onRetry={onRetry}
        onDelete={onDelete}
      />
    </div>
  )
}

export default EnrollmentDetail
